import { destroy, submit } from "redux-form/immutable"
import moment from "moment"
import queryString from "query-string"
import * as Sentry from "@sentry/browser"

// Redux
import {
    segueToEditContact,
    segueToEditOrganization,
    popPage,
    segueToEditInteraction,
    segueToEditDonation,
    segueToEditRelationship,
    segueToEditComment,
} from "app/static/frontend/global-action-creators"
import { clearForm } from "shared/forms/LogInteraction/action-creators"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"
import {
    formDetailsDict,
    getUserActionDescription,
    getUserActionDataTypesFromInteraction,
} from "shared/forms/LogInteraction/selectors"
import { getOfficialSelect } from "app/static/frontend/forms/desktop-selectors"

// Components
import {
    AmendmentSelect,
    BillSelect,
    ContactSelect,
    DocumentSelect,
    OfficialSelect,
    IssueManagementSelect,
    PressContactSelect,
    PublicOrganizationSelect,
    RegulationSelect,
    StafferSelect,
    VoteSelect,
    OutletSelect,
    ArticleSelect,
} from "app/static/frontend/selects/containers/PieSelect"

import {
    LOG_INTERACTION_FORM_KEY,
    LOG_RELATIONSHIP_FORM_KEY,
    LOG_COMMENT_FORM_KEY,
    LOG_DONATION_FORM_KEY,
} from "shared/imports/sharedConstants"

/**
 * This file includes all of the functions used by all web-based interaction logger forms.
 */

const recordUserActions = (props) => {
    let actionType
    switch (props.form) {
        case LOG_INTERACTION_FORM_KEY:
            actionType = DjangIO.app.useractions.types.UserActionType.new_interaction
            break
        case LOG_COMMENT_FORM_KEY:
            actionType = DjangIO.app.useractions.types.UserActionType.new_comment
            break
        case LOG_RELATIONSHIP_FORM_KEY:
            actionType = DjangIO.app.useractions.types.UserActionType.new_relationship
            break
        default:
            break
    }

    /*
     * Interactions can involve participants of multiple QDTs.
     * If this is the case, we can fire a separate UserAction for each QDT
     * that lists the region/most_recent_region of all participants of that type
     */
    const quorumDataTypes = getUserActionDataTypesFromInteraction(props)
    quorumDataTypes.forEach((quorumDataType) => {
        let regions = []
        if ([LOG_INTERACTION_FORM_KEY, LOG_RELATIONSHIP_FORM_KEY].includes(props.form)) {
            switch (quorumDataType) {
                case DjangIO.app.models.QuorumDataType.person:
                    regions = props.officialsMostRecentRegions
                    break
                case DjangIO.app.models.QuorumDataType.staffer:
                    regions = props.staffersMostRecentRegions
                    break
                case DjangIO.app.models.QuorumDataType.supporter:
                    regions = props.contactsRegions
                    break
                default:
                    break
            }
        } else if (props.form === LOG_COMMENT_FORM_KEY) {
            /*
             * Comments could be one of many different QDTs, but their regions are usually stored in commentRegions.
             * If you are looking to add support for more QDTs, ensure that their PieSelect implementation includes
             * the region/most_recent_region as an extraField.
             */
            regions = props.commentsRegions
        }

        if (actionType && quorumDataType) {
            sendUserActionPost({
                userActionType: actionType,
                description: `${getUserActionDescription(props.form, props.type)} - ${window.location.pathname}`,
                quorumDataType: quorumDataType.value,
                regions,
            })
        }
    })
}

// functions called by redux-form
export const createOnSubmit = (form) => async (immutableValues, dispatch, props) => {
    const values = immutableValues.toJS()
    const result = await dispatch(formDetailsDict[form].createFunction(values, props))
    recordUserActions(props)
    return result
}

/**
 * Handles the swaling after we have successfully saved a new note. Here was ask the user whether that want to (A) pop back to
 * wherever place they navigated to logger form from or to (B) log another/a new note.
 * @param  {string} form is the redux-form key
 */
export const createOnSubmitSuccess = (form) => (result, dispatch, props) => {
    swal({
        closeOnClickOutside: false,
        closeOnEsc: false,
        icon: "success",
        title: `${props.isEdit ? "Edited" : "Submitted"} ${formDetailsDict[form].name}`,
        text: `${formDetailsDict[form].name} ${props.isEdit ? "edited" : "submitted"} successfully.`,
        buttons: {
            goBack: "Go Back",
            // if user just edited a note, then ask about creating 'a new' note, o.w. word is 'another'
            defaultLogAnother: `${formDetailsDict[form].verb} ${props.isEdit ? "a new" : "another"} ${
                formDetailsDict[form].name
            }`,
        },
    }).then((value) => {
        if (value === "goBack") {
            // popPage to go back to wherever the user navigated to logger form from
            dispatch(popPage())
            // destroy redux-form's slice in store for this form
            props.destroy()
            // clear the interactionLogger slice of the store
            props.clearForm()
        } else {
            // if we we're editing a note and want to log another, then need a fresh form without datumId in the url params
            if (props.isEdit) {
                props.clearLoadedNote()
                dispatch(popPage())
                switch (form) {
                    case LOG_INTERACTION_FORM_KEY:
                        dispatch(segueToEditInteraction())
                        break
                    case LOG_DONATION_FORM_KEY:
                        dispatch(segueToEditDonation())
                        break
                    case LOG_COMMENT_FORM_KEY:
                        dispatch(segueToEditComment())
                        break
                    case LOG_RELATIONSHIP_FORM_KEY:
                        dispatch(segueToEditRelationship())
                        break
                    default:
                        break
                }
            }
            // set the redux-form values to the initialValues
            props.reset()
            // reset initial datetime to the current datetime
            props.change("datetime", moment())
            // scroll to the top of the page
            window.scrollTo(0, 0)
        }
    })
}

export const createOnSubmitFail = () => (errors, dispatch, submitError, props) => {
    // We need extra information to diagnose a tricky bug plaguing PG&E
    Sentry.captureException(new Error("LoggerFormSubmitError"), {
        contexts: {
            errors,
            submitError,
            props,
        },
    })
    swal({
        icon: "error",
        title: "We're sorry!",
        text: "There was an error editing your note.",
    })
}

/**
 * Handles onClick for create contact button -- used by web and mobile (which is why you've got to pass in the segue/Action).
 * It segues to ContactForm and give it params to populate this form with its results if new contact is created
 * @param  {string} form                redux form key (aka name of slice within redux-form's getState().form slice)
 * @param  {function} goToContactForm)  takes in an object with a 'form' = redux form key and a 'field' = name of the value
 */
export const createOnClickCreateContact = (form) => (e) => {
    e.stopPropagation()
    return (dispatch) => dispatch(segueToEditContact({ form, field: "contacts" }))
}

// same as onClickCreateContact but for the PublicOrganizationForm
export const createOnClickCreateOrg = (form) => (e) => {
    e.stopPropagation()
    return (dispatch) => dispatch(segueToEditOrganization({ form, field: "orgs" }))
}

export const createOnClickCancel = (form) => () => (dispatch) => {
    // Destroys the redux form. Have to be explicitly called since we set destroyOnUnmount to false
    dispatch(destroy(form))
    dispatch(popPage())
    // Clear out the interactionLogger slice of the store as well
    dispatch(clearForm())
}

export const createOnClickSubmit = (form) => () => (dispatch, getState) => {
    const errors = Object.values(getState().form.getIn([form, "syncErrors"]) || {})

    // if there are errors, then swal
    if (errors && errors.length) {
        swal({
            icon: "error",
            text: errors.join(", "),
        })
    } else {
        // otherwise, submit and clear the form state from state.interactionLogger slice
        dispatch(submit(form))
    }
}

export const commentTypesAndSelects = {
    [DjangIO.app.models.QuorumDataType.amendment.value]: AmendmentSelect,
    [DjangIO.app.models.QuorumDataType.bill.value]: BillSelect,
    [DjangIO.app.models.QuorumDataType.document.value]: DocumentSelect,
    [DjangIO.app.models.QuorumDataType.issue_management.value]: IssueManagementSelect,
    [DjangIO.app.models.QuorumDataType.person.value]: OfficialSelect,
    [DjangIO.app.models.QuorumDataType.project.value]: IssueManagementSelect,
    [DjangIO.app.models.QuorumDataType.press_contact.value]: PressContactSelect,
    [DjangIO.app.models.QuorumDataType.outlet.value]: OutletSelect,
    [DjangIO.app.models.QuorumDataType.public_organization.value]: PublicOrganizationSelect,
    [DjangIO.app.models.QuorumDataType.regulation.value]: RegulationSelect,
    [DjangIO.app.models.QuorumDataType.staffer.value]: StafferSelect,
    [DjangIO.app.models.QuorumDataType.supporter.value]: ContactSelect,
    [DjangIO.app.models.QuorumDataType.vote.value]: VoteSelect,
    [DjangIO.app.models.QuorumDataType.media.value]: ArticleSelect,
    [DjangIO.app.models.QuorumDataType.person.value]: getOfficialSelect(),
}

export const getCommentSelectComponent = (type) => commentTypesAndSelects[type] || StafferSelect

export const loggersForNoteFLV = (profileQdt, profileId, isInSearch) => {
    let field
    const loggersAvailable = {
        interaction: false,
        donation: false,
        relationship: false,
        comment: false,
    }

    const shouldDisplayInteraction = Userdata.should_display_interaction_form
    const shouldDisplayRecordDonation = Userdata.should_display_record_donation_form
    const shouldDisplayRelationship = Userdata.should_display_relationship_form

    switch (profileQdt) {
        case DjangIO.app.models.QuorumDataType.person.value:
            field = "officials"
            loggersAvailable.interaction = shouldDisplayInteraction
            loggersAvailable.donation = shouldDisplayRecordDonation
            loggersAvailable.relationship = shouldDisplayRelationship
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.staffer.value:
            field = "staff"
            loggersAvailable.interaction = shouldDisplayInteraction
            loggersAvailable.relationship = shouldDisplayRelationship
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.supporter.value:
            field = "contacts"
            loggersAvailable.interaction = shouldDisplayInteraction
            loggersAvailable.donation = shouldDisplayRecordDonation
            loggersAvailable.relationship = shouldDisplayRelationship
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.public_organization.value:
            field = "orgs"
            loggersAvailable.interaction = shouldDisplayInteraction
            loggersAvailable.donation = shouldDisplayRecordDonation
            loggersAvailable.relationship = shouldDisplayRelationship
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.press_contact.value:
            field = "pressContacts"
            loggersAvailable.interaction = shouldDisplayInteraction
            loggersAvailable.relationship = shouldDisplayRelationship
            break
        case DjangIO.app.models.QuorumDataType.bill.value:
            field = DjangIO.app.models.QuorumDataType.bill.note_through_field
            loggersAvailable.interaction = shouldDisplayInteraction
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.document.value:
            field = DjangIO.app.models.QuorumDataType.document.note_through_field
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.vote.value:
            field = DjangIO.app.models.QuorumDataType.vote.note_through_field
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.amendment.value:
            field = DjangIO.app.models.QuorumDataType.amendment.note_through_field
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.project.value:
        case DjangIO.app.models.QuorumDataType.issue_management.value:
            field = DjangIO.app.models.QuorumDataType.project.note_through_field
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.regulation.value:
            field = DjangIO.app.models.QuorumDataType.regulation.note_through_field
            loggersAvailable.comment = true
            break
        case DjangIO.app.models.QuorumDataType.media.value:
            field = DjangIO.app.models.QuorumDataType.media.note_through_field
            loggersAvailable.comment = true
            break
        default:
            if (isInSearch) {
                // plain old Notes search
                loggersAvailable.interaction = shouldDisplayInteraction
                loggersAvailable.donation = shouldDisplayRecordDonation
                loggersAvailable.relationship = shouldDisplayRelationship
                loggersAvailable.comment = true
            }
            break
    }

    return [field, loggersAvailable]
}

export const initializeFormWithUrlParams = (locationSearch, props) => {
    let { qdt, value, comment } = queryString.parse(locationSearch) // eslint-disable-line prefer-const

    // qdt is QDT value as a string, so parse as an int
    qdt = parseInt(qdt)
    if (Number.isNaN(qdt) || !DjangIO.app.models.QuorumDataType.by_value(qdt)) {
        // should not happen, but in case some user tries messing aroung in the url, check that qdt is real
        return
    }

    const [field] = loggersForNoteFLV(qdt)
    if (!field) {
        // making a note from scratch or editing an existing note
        return
    }

    // removing ending '/', really feel like queryString should be able to do this...
    if (value && value.split("/").length) {
        ;[value] = value.split("/")
    }
    // try to parse as number for case that value is an id
    value = Number.isNaN(parseInt(value)) ? value : parseInt(value)
    if (field && value) {
        if (comment === "true") {
            props.array.push("commentItems", value)
            props.change("type", qdt)
        } else {
            props.array.push(field, value)
        }
    }
}

export const getUrlParamsToInitializeForm = (qdtToPopulate, value, toCommentForm = false) => {
    // when navigating from profile to loggers, pathname is in form: '/<profile>/<id>/notes/'
    if (window.location.pathname.split("/").length === 5) {
        return `?qdt=${qdtToPopulate}&comment=${Boolean(toCommentForm)}&value=${value}` // does not handle multiple values
    }
    return undefined
}

/**
 * Every desktop logger form does the same basic things on mount:
 *     Clear the store if it is populated with old stuff
 *     Load projects and custom fields into the store
 *     If editing an existing note, load the existing note
 *     Otherwise, check is fields are being set by url params, set the datetime to the current time
 */
export const onLoggerFormDidMount = (props, loadExistingNoteFunc) => {
    const {
        getProjects,
        loadCustomFields,
        isEdit,
        params: { datumId },
        change,
        datetime,
    } = props

    // check if currently loaded note is old - if a user used BackButton when editing a different interaction
    // then we want to show loading spinner while fetching new info rather than old stuff
    if (props.noteDetails && datumId !== props.noteDetails.id) {
        props.clearForm()
    }

    getProjects()
    if (loadCustomFields) {
        loadCustomFields(Userdata.organization_id)
    }

    if (isEdit) {
        // if this is an existing note that we are editing, then load the note
        loadExistingNoteFunc(datumId)
    } else {
        // can't set datetime in intialValues of redux-form selector since we need enableReinitialize true, so just set it on mount
        // but make sure to only set it on the first onMount (searchify unmounts this so don't want to reset the user's value in that case)
        if (datetime === undefined) {
            change("datetime", moment())
        }
        // if user got to this form by way of another form, then the form might have props for us to start with in the url params
        initializeFormWithUrlParams(props.location.search, props)
    }
}
